import React, { useState, useEffect, useCallback } from 'react';
import styled from 'styled-components';
import { 
  X, 
  MessageCircle, 
  Users, 
  Type, 
  Volume2, 
  Paintbrush, 
  Eye,
  Clock,
  Trophy,
  Ship,
  Download,
  CheckCircle,
  AlertCircle
} from 'lucide-react';
import ChatModal from './ChatModal';
import CategorySelection from './CategorySelection';
import ModeSelection from './ModeSelection';
import StakeSetting from './StakeSetting';
import WaitingRoom from './WaitingRoom';
import LiveCompetition from './LiveCompetition';
import ShipAnimation from './ShipAnimation';
import useAutoScroll from '../../hooks/useAutoScroll';
import ViewProfileModal from './ViewProfileModal';

// Types
interface User {
  id: string;
  name: string;
  accountType: 'student' | 'public';
  gradeLevel?: number;
  university?: string;
  rating: number;
  wins: number;
  totalMatches: number;
  avatar?: string;
  profilePicture?: string;
  isOnline: boolean;
}

interface Competitor extends User {
  lastSeen?: string;
  currentStake?: number;
}

interface Question {
  id: string;
  question: string;
  options: string[];
  correct: string;
  time: number;
  category: string;
  subcategory: string;
}

interface CompetitionData {
  id: string;
  competitor: Competitor;
  category: string;
  subcategory: string;
  mode: string;
  stake: number;
  questions: Question[];
  prize: number;
  appFee: number;
}

type ChallengeStep = 'dashboard' | 'chat' | 'categories' | 'mode' | 'stake' | 'waiting' | 'competition' | 'evaluation';

// Styled Components
const InterfaceOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, #1e3c72 0%, #2a5298 100%);
  z-index: 1000;
  overflow-y: auto;
  padding: 20px;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 30px;
  padding: 20px;
  background: rgba(255, 255, 255, 0.1);
  backdrop-filter: blur(10px);
  border-radius: 15px;
  border: 1px solid rgba(255, 255, 255, 0.2);
`;

const Title = styled.h1`
  font-size: 2rem;
  font-weight: 700;
  color: white;
  margin: 0;
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.1);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: white;
  padding: 10px 20px;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.05);
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 8px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 4px;
  margin-bottom: 20px;
  overflow: hidden;
`;

const ProgressFill = styled.div<{ progress: number }>`
  height: 100%;
  background: linear-gradient(90deg, #4caf50, #8bc34a);
  border-radius: 4px;
  transition: width 0.5s ease;
  width: ${props => props.progress}%;
`;

const StepIndicator = styled.div`
  display: flex;
  justify-content: center;
  gap: 10px;
  margin-bottom: 30px;
`;

const StepDot = styled.div<{ active: boolean; completed: boolean }>`
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: ${props => {
    if (props.completed) return '#4caf50';
    if (props.active) return '#ffd700';
    return 'rgba(255, 255, 255, 0.3)';
  }};
  margin: 0 8px;
  transition: all 0.3s ease;
  transform: ${props => props.active ? 'scale(1.2)' : 'scale(1)'};
`;

const ContentArea = styled.div`
  background: rgba(255, 255, 255, 0.05);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  padding: 30px;
  border: 1px solid rgba(255, 255, 255, 0.1);
  min-height: 500px;
`;

const CompetitionInterface: React.FC<{
  isOpen: boolean;
  onClose: () => void;
  selectedCompetitor?: Competitor;
}> = ({ isOpen, onClose, selectedCompetitor }) => {
  console.log('CompetitionInterface render:', { 
    isOpen, 
    challengeStep: 'not set yet', 
    currentQuestion: 'not set yet',
    timestamp: new Date().toISOString()
  });
  
  // State Management
  const [challengeStep, setChallengeStep] = useState<ChallengeStep>('dashboard');
  const [showChatModal, setShowChatModal] = useState(false);
  const [chatMessages, setChatMessages] = useState<any[]>([]);
  const [newMessage, setNewMessage] = useState('');
  
  // View Profile Modal State
  const [showViewProfileModal, setShowViewProfileModal] = useState(false);
  const [selectedProfileCompetitor, setSelectedProfileCompetitor] = useState<Competitor | null>(null);
  
  const [selectedCategory, setSelectedCategory] = useState('');
  const [selectedSubcategory, setSelectedSubcategory] = useState('');
  const [selectedMode, setSelectedMode] = useState('');
  const [stakeAmount, setStakeAmount] = useState(0);
  
  const [competitionData, setCompetitionData] = useState<CompetitionData | null>(null);
  const [currentReady, setCurrentReady] = useState(false);
  const [opponentReady, setOpponentReady] = useState(false);
  
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);
  const [userAnswers, setUserAnswers] = useState<string[]>([]);
  const [selectedAnswer, setSelectedAnswer] = useState('');
  
  const [showShipAnimation, setShowShipAnimation] = useState(false);
  const [shipPosition, setShipPosition] = useState(0);
  const [evaluationPhase, setEvaluationPhase] = useState<'sailing' | 'capsizing' | 'capsized' | 'results'>('sailing');
  const [captainLogs, setCaptainLogs] = useState('');
  const [finalResults, setFinalResults] = useState<{
    userScore: number;
    opponentScore: number;
    won: boolean;
    prize: number;
  } | null>(null);
  
  const { scrollToTop, scrollToContinueButton } = useAutoScroll();

  // Use default competitor if none provided
  const defaultCompetitor: Competitor = {
    id: 'default',
    name: 'AI Opponent',
    accountType: 'public',
    university: 'AI University',
    rating: 4.0,
    wins: 10,
    totalMatches: 20,
    isOnline: true,
    currentStake: 100
  };

  const activeCompetitor = selectedCompetitor || defaultCompetitor;

  // Mock competitors data
  const [competitors] = useState<Competitor[]>([
    {
      id: '1',
      name: 'John Kariuki',
      accountType: 'student',
      gradeLevel: 4,
      rating: 4.2,
      wins: 8,
      totalMatches: 15,
      isOnline: true,
      currentStake: 100
    },
    {
      id: '2',
      name: 'Mary Wanjiku',
      accountType: 'public',
      university: 'University of Nairobi',
      rating: 4.7,
      wins: 15,
      totalMatches: 22,
      isOnline: true,
      currentStake: 250
    },
    {
      id: '3',
      name: 'Peter Maina',
      accountType: 'student',
      gradeLevel: 5,
      rating: 3.8,
      wins: 6,
      totalMatches: 12,
      isOnline: false,
      currentStake: 80
    },
    {
      id: '4',
      name: 'Grace Achieng',
      accountType: 'public',
      university: 'Kenyatta University',
      rating: 4.5,
      wins: 12,
      totalMatches: 18,
      isOnline: true,
      currentStake: 150
    },
    {
      id: '5',
      name: 'David Kiprop',
      accountType: 'student',
      gradeLevel: 6,
      rating: 4.1,
      wins: 9,
      totalMatches: 16,
      isOnline: false,
      currentStake: 120
    }
  ]);

  // Handle competitor selection
  const handleCompetitorSelect = (competitor: Competitor) => {
    // In a real app, you would set the selected competitor here
    // For now, we'll simulate selecting a competitor
    console.log('Selected competitor:', competitor);
    setChallengeStep('chat');
  };

  // Handle view profile
  const handleViewProfile = (competitor: Competitor) => {
    setSelectedProfileCompetitor(competitor);
    setShowViewProfileModal(true);
  };

  const handleViewProfileModalClose = () => {
    setShowViewProfileModal(false);
    setSelectedProfileCompetitor(null);
  };

  // Progress calculation
  const getProgress = () => {
    const steps: ChallengeStep[] = ['dashboard', 'chat', 'categories', 'mode', 'stake', 'waiting', 'competition', 'evaluation'];
    const currentIndex = steps.indexOf(challengeStep);
    return ((currentIndex + 1) / steps.length) * 100;
  };

  // Step management
  const nextStep = useCallback(() => {
    const steps: ChallengeStep[] = ['dashboard', 'chat', 'categories', 'mode', 'stake', 'waiting', 'competition', 'evaluation'];
    const currentIndex = steps.indexOf(challengeStep);
    if (currentIndex < steps.length - 1) {
      setChallengeStep(steps[currentIndex + 1]);
      
      // Auto-scroll to top when transitioning to new step
      setTimeout(() => {
        scrollToTop(undefined, { behavior: 'smooth' });
      }, 100);
    }
  }, [challengeStep, scrollToTop]);

  const previousStep = useCallback(() => {
    const steps: ChallengeStep[] = ['dashboard', 'chat', 'categories', 'mode', 'stake', 'waiting', 'competition', 'evaluation'];
    const currentIndex = steps.indexOf(challengeStep);
    if (currentIndex > 0) {
      setChallengeStep(steps[currentIndex - 1]);
    }
  }, [challengeStep]);

  // Chat functionality
  const handleSendMessage = (message: string) => {
    const newMsg = {
      id: Date.now(),
      text: message,
      sender: 'user',
      timestamp: new Date()
    };
    setChatMessages(prev => [...prev, newMsg]);
    
    // Simulate opponent response
    setTimeout(() => {
      const opponentMsg = {
        id: Date.now() + 1,
        text: 'I accept your challenge! Let\'s compete!',
        sender: 'opponent',
        timestamp: new Date()
      };
      setChatMessages((prev: any[]) => [...prev, opponentMsg]);
      
      // Auto-advance to next step
      setTimeout(() => {
        nextStep();
      }, 2000);
    }, 1000);
  };

  // Category selection
  const handleCategorySelect = (category: string, subcategory: string) => {
    setSelectedCategory(category);
    setSelectedSubcategory(subcategory);
    nextStep();
  };

  // Mode selection
  const handleModeSelect = (mode: string) => {
    setSelectedMode(mode);
    nextStep();
  };

  // Stake setting
  const handleStakeConfirm = (stake: number) => {
    console.log('Stake confirmed:', { stake, selectedCategory, selectedSubcategory, selectedMode });
    setStakeAmount(stake);
    
    // Calculate competition parameters
    const appFee = stake * 0.1;
    const prize = stake * 2 - appFee;
    
    // Generate questions based on stake and difficulty
    const questions = generateQuestions(stake, selectedCategory, selectedSubcategory);
    console.log('Generated questions:', questions.length);
    
    setCompetitionData({
      id: Date.now().toString(),
      competitor: activeCompetitor,
      category: selectedCategory,
      subcategory: selectedSubcategory,
      mode: selectedMode,
      stake,
      questions,
      prize,
      appFee
    });
    
    console.log('Competition data set, moving to waiting room');
    setCurrentReady(true);
    // Simulate opponent being ready after a short delay
    setTimeout(() => {
      setOpponentReady(true);
    }, 2000);
    nextStep();
  };

  // Generate questions based on stake and difficulty
  const generateQuestions = (stake: number, category: string, subcategory: string): Question[] => {
    let difficulty: 'easy' | 'medium' | 'hard';
    let questionCount: number;
    
    if (stake >= 1 && stake <= 10) {
      difficulty = 'easy';
      questionCount = 10;
    } else if (stake >= 50 && stake <= 99) {
      difficulty = 'medium';
      questionCount = 25;
    } else {
      difficulty = 'hard';
      questionCount = 30;
    }
    
    // Mock questions - in real app, these would come from database
    const questions: Question[] = [];
    for (let i = 0; i < questionCount; i++) {
      questions.push({
        id: i.toString(),
        question: `Sample ${difficulty} question ${i + 1} in ${subcategory}?`,
        options: ['Option A', 'Option B', 'Option C', 'Option D'],
        correct: 'Option A',
        time: 30,
        category,
        subcategory
      });
    }
    
    return questions;
  };



  // Start competition when both players are ready
  const startCompetition = useCallback(() => {
    console.log('startCompetition called with:', { 
      challengeStep,
      competitionData: !!competitionData,
      questionsCount: competitionData?.questions?.length 
    });
    
    if (!competitionData) {
      console.error('No competition data available');
      return;
    }
    
    if (challengeStep !== 'waiting') {
      console.log('Competition already started or in different step:', challengeStep);
      return;
    }
    
    // Use existing questions from competitionData instead of regenerating
    const questions = competitionData.questions;
    
    if (!questions || questions.length === 0) {
      console.error('No questions available for competition');
      return;
    }

    // Transition to competition step
    console.log('Setting challengeStep to competition');
    setChallengeStep("competition");
    
    // Only reset question state if starting fresh
    if (currentQuestion === 0 && userAnswers.length === 0) {
      setCurrentQuestion(0);
      setTimeLeft(questions[0]?.time || 30);
      setUserAnswers([]);
      setSelectedAnswer("");
    }
    
    console.log('Competition started successfully, challengeStep should now be: competition');
  }, [challengeStep, competitionData, currentQuestion, userAnswers]);

  // Handle countdown completion
  const handleCountdownComplete = () => {
    startCompetition();
  };

  // Finish competition and calculate results
  const finishCompetition = useCallback(() => {
    console.log('finishCompetition called with:', {
      competitionData: !!competitionData,
      currentQuestion,
      questionsLength: competitionData?.questions?.length,
      userAnswersLength: userAnswers.length
    });
    
    if (!competitionData) {
      console.error('No competition data available for finishing competition');
      return;
    }
    
    // Safety check: ensure we don't process if currentQuestion is out of bounds
    if (currentQuestion >= (competitionData.questions?.length || 0)) {
      console.error('Current question index out of bounds, cannot finish competition properly:', {
        currentQuestion,
        questionsLength: competitionData.questions?.length
      });
      // Force reset to a safe state
      setCurrentQuestion(0);
      setChallengeStep('dashboard');
      return;
    }
    
    // Calculate scores
    let userScore = 0;
    let opponentScore = 0;
    
    competitionData.questions.forEach((q, index) => {
      if (userAnswers[index] === q.correct) userScore++;
      // Simulate opponent answers (random for demo)
      const opponentAnswer = q.options[Math.floor(Math.random() * q.options.length)];
      if (opponentAnswer === q.correct) opponentScore++;
    });
    
    const won = userScore > opponentScore;
    const prize = won ? competitionData.prize : 0;
    
    console.log('Competition results calculated:', {
      userScore,
      opponentScore,
      won,
      prize,
      totalQuestions: competitionData.questions.length
    });
    
    setFinalResults({
      userScore,
      opponentScore,
      won,
      prize
    });
    
    // Generate captain's logs
    const logs = generateCaptainLogs(competitionData.questions, userAnswers, userScore, opponentScore);
    setCaptainLogs(logs);
    
    // Start ship animation
    setShowShipAnimation(true);
    console.log('Moving to evaluation phase');
    nextStep();
  }, [competitionData, userAnswers, currentQuestion, nextStep]);

  // Live competition
  const handleAnswerSelect = useCallback((answer: string) => {
    console.log('Answer selected:', { answer, currentQuestion, previousAnswer: selectedAnswer });
    setSelectedAnswer(answer);
  }, [currentQuestion, selectedAnswer]);

  const handleAnswerSubmit = useCallback(() => {
    console.log('handleAnswerSubmit called with:', { 
      selectedAnswer, 
      currentQuestion, 
      totalQuestions: competitionData?.questions?.length,
      questionsLength: competitionData?.questions?.length,
      maxIndex: competitionData?.questions ? competitionData.questions.length - 1 : -1
    });
    
    if (selectedAnswer) {
      const newAnswers = [...userAnswers];
      newAnswers[currentQuestion] = selectedAnswer;
      setUserAnswers(newAnswers);
      
      console.log('Answer recorded:', { question: currentQuestion, answer: selectedAnswer, newAnswers });
      
      // Safety check: ensure currentQuestion is within bounds
      if (currentQuestion >= (competitionData?.questions?.length || 0)) {
        console.error('Current question index out of bounds, finishing competition immediately:', {
          currentQuestion,
          questionsLength: competitionData?.questions?.length
        });
        finishCompetition();
        return;
      }
      
      // Move to next question or finish
      if (currentQuestion < (competitionData?.questions.length || 0) - 1) {
        console.log('Moving to next question:', { current: currentQuestion, next: currentQuestion + 1 });
        setCurrentQuestion(prev => {
          const next = prev + 1;
          // Safety check: ensure next index is within bounds
          if (next >= (competitionData?.questions?.length || 0)) {
            console.error('Attempted to set currentQuestion to invalid index:', {
              prev,
              next,
              questionsLength: competitionData?.questions?.length,
              maxValidIndex: (competitionData?.questions?.length || 0) - 1
            });
            // Don't update if it would be out of bounds
            return prev;
          }
          console.log('Setting currentQuestion from', prev, 'to', next);
          return next;
        });
        // selectedAnswer and timeLeft will be reset by useEffect when currentQuestion changes
        console.log('State updates scheduled - currentQuestion will be updated');
      } else {
        console.log('Competition finished, calling finishCompetition');
        // Competition finished
        finishCompetition();
      }
    } else {
      console.log('No answer selected, cannot submit');
    }
  }, [selectedAnswer, currentQuestion, competitionData?.questions?.length, userAnswers, finishCompetition]);

  // Generate captain's logs
  const generateCaptainLogs = (questions: Question[], userAnswers: string[], userScore: number, opponentScore: number) => {
    let logs = "=== CAPTAIN'S LOGS ===\n";
    logs += `Competition Date: ${new Date().toLocaleString()}\n`;
    logs += `Competitor 1: You (Score: ${userScore}/${questions.length})\n`;
    logs += `Competitor 2: ${activeCompetitor.name} (Score: ${opponentScore}/${questions.length})\n\n`;
    
    questions.forEach((q, index) => {
      logs += `Question ${index + 1}: ${q.question}\n`;
      logs += `Correct Answer: ${q.correct}\n`;
      logs += `Your Answer: ${userAnswers[index] || "No answer"}\n`;
      logs += `Your Result: ${userAnswers[index] === q.correct ? "✓ Correct" : "✗ Wrong"}\n`;
      logs += "---\n";
    });
    
    return logs;
  };

  // Download captain's logs
  const downloadCaptainLogs = () => {
    const blob = new Blob([captainLogs], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `captain-logs-${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Close competition
  const closeCompetition = () => {
    setChallengeStep('dashboard');
    setShowChatModal(false);
    setSelectedCategory('');
    setSelectedSubcategory('');
    setSelectedMode('');
    setStakeAmount(0);
    setCompetitionData(null);
    setOpponentReady(false);
    setCurrentReady(false);
    setCurrentQuestion(0);
    setTimeLeft(30);
    setUserAnswers([]);
    setSelectedAnswer('');
    setShowShipAnimation(false);
    setShipPosition(0);
    setEvaluationPhase('sailing');
    setCaptainLogs('');
    setFinalResults(null);
    onClose();
  };

  // Auto-advance timer
  useEffect(() => {
    if (challengeStep === 'competition' && timeLeft > 0) {
      const timer = setTimeout(() => {
        setTimeLeft(prev => {
          const newTime = prev - 1;
          if (newTime === 0) {
            // Auto-submit when time runs out, but only if we're still within bounds
            setTimeout(() => {
              // Get current values to avoid stale closure issues
              const currentChallengeStep = challengeStep;
              const currentQuestionIndex = currentQuestion;
              const currentCompetitionData = competitionData;
              
              if (currentChallengeStep === 'competition' && 
                  currentCompetitionData?.questions && 
                  currentQuestionIndex >= 0 && 
                  currentQuestionIndex < currentCompetitionData.questions.length) {
                console.log('Auto-submitting answer due to time running out for question:', currentQuestionIndex);
                handleAnswerSubmit();
              } else {
                console.warn('Cannot auto-submit: question index out of bounds or competition ended:', {
                  currentQuestionIndex,
                  questionsLength: currentCompetitionData?.questions?.length,
                  challengeStep: currentChallengeStep
                });
              }
            }, 100);
          }
          return newTime;
        });
      }, 1000);
      
      return () => clearTimeout(timer);
    }
  }, [challengeStep, timeLeft, handleAnswerSubmit, competitionData]);

  // Auto-start competition when both players are ready
  useEffect(() => {
    console.log('useEffect triggered:', { currentReady, opponentReady, challengeStep });
    if (currentReady && opponentReady && challengeStep === 'waiting') {
      console.log('Both players ready, starting competition...');
      startCompetition();
    }
  }, [currentReady, opponentReady, challengeStep, startCompetition]);

  // Debug effect for challengeStep changes
  useEffect(() => {
    console.log('ChallengeStep changed to:', challengeStep);
    if (challengeStep === 'competition') {
      console.log('Competition step reached, checking data:', {
        competitionData: !!competitionData,
        questions: competitionData?.questions?.length,
        currentQuestion
      });
    }
  }, [challengeStep, competitionData, currentQuestion]);

  // Reset selectedAnswer when currentQuestion changes
  useEffect(() => {
    if (challengeStep === 'competition') {
      setSelectedAnswer('');
      // Reset timer to full time for the new question
      if (competitionData?.questions && 
          currentQuestion >= 0 && 
          currentQuestion < competitionData.questions.length &&
          competitionData.questions[currentQuestion]?.time) {
        setTimeLeft(competitionData.questions[currentQuestion].time);
      } else {
        console.warn('Invalid question index or missing question data, using fallback timer:', {
          currentQuestion,
          questionsLength: competitionData?.questions?.length,
          questionExists: !!competitionData?.questions?.[currentQuestion]
        });
        setTimeLeft(30); // fallback
      }
      console.log('Reset selectedAnswer and timer for new question:', currentQuestion);
    }
  }, [currentQuestion, challengeStep, competitionData]);

  // Monitor currentQuestion changes for debugging
  useEffect(() => {
    if (challengeStep === 'competition' && competitionData?.questions) {
      if (currentQuestion < 0 || currentQuestion >= competitionData.questions.length) {
        console.error('currentQuestion is out of bounds:', {
          currentQuestion,
          questionsLength: competitionData.questions.length,
          validRange: `0 to ${competitionData.questions.length - 1}`,
          stack: new Error().stack
        });
      } else {
        console.log('currentQuestion changed to valid index:', {
          currentQuestion,
          questionsLength: competitionData.questions.length,
          questionExists: !!competitionData.questions[currentQuestion]
        });
      }
    }
  }, [currentQuestion, challengeStep, competitionData]);

  // Test function to verify compete functionality
  const testCompeteFlow = () => {
    console.log('Testing compete flow...');
    setChallengeStep('categories');
  };

  if (!isOpen) return null;

  return (
    <InterfaceOverlay>
      <Header>
        <Title>Competition Challenge</Title>
        <CloseButton onClick={closeCompetition}>
          <X size={20} />
          Close
        </CloseButton>
      </Header>

      <ProgressBar>
        <ProgressFill progress={getProgress()} />
      </ProgressBar>

      <StepIndicator>
        {['dashboard', 'chat', 'categories', 'mode', 'stake', 'waiting', 'competition', 'evaluation'].map((step, index) => (
          <StepDot
            key={step}
            active={challengeStep === step}
            completed={['dashboard', 'chat', 'categories', 'mode', 'stake', 'waiting', 'competition', 'evaluation'].indexOf(challengeStep) > index}
          />
        ))}
      </StepIndicator>

      <ContentArea>
        {challengeStep === 'dashboard' && (
          <div>
            <h2 style={{ color: 'white', marginBottom: '20px', textAlign: 'center' }}>Available Competitors</h2>
            <div style={{ display: 'grid', gap: '15px', maxWidth: '600px', margin: '0 auto' }}>
              {competitors.map((competitor) => (
                <div
                  key={competitor.id}
                  style={{
                    background: 'rgba(255, 255, 255, 0.1)',
                    padding: '20px',
                    borderRadius: '15px',
                    border: '1px solid rgba(255, 255, 255, 0.2)',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <h3 style={{ color: 'white', margin: '0 0 5px 0' }}>{competitor.name}</h3>
                    <p style={{ color: 'rgba(255, 255, 255, 0.8)', margin: '0 0 5px 0', fontSize: '14px' }}>
                      {competitor.accountType === 'student' 
                        ? `Grade ${competitor.gradeLevel}` 
                        : competitor.university
                      }
                    </p>
                    
                    {/* Enhanced Educational Information */}
                    {competitor.educationalInfo && (
                      <p style={{ color: 'rgba(255, 255, 255, 0.7)', margin: '0 0 5px 0', fontSize: '12px' }}>
                        {competitor.educationalInfo.isCurrentlyStudying && competitor.educationalInfo.institution && competitor.educationalInfo.courseOfStudy && competitor.educationalInfo.yearOfStudy ? (
                          `${competitor.educationalInfo.yearOfStudy === 1 ? '1st' : 
                            competitor.educationalInfo.yearOfStudy === 2 ? '2nd' : 
                            competitor.educationalInfo.yearOfStudy === 3 ? '3rd' : 
                            competitor.educationalInfo.yearOfStudy + 'th'} Year ${competitor.educationalInfo.courseOfStudy}`
                        ) : competitor.educationalInfo.institution ? (
                          `Studying at ${competitor.educationalInfo.institution}`
                        ) : null}
                      </p>
                    )}
                    
                    {/* Location Information */}
                    {competitor.personalInfo && (
                      <p style={{ color: 'rgba(255, 255, 255, 0.6)', margin: '0 0 5px 0', fontSize: '11px' }}>
                        📍 {competitor.personalInfo.city || competitor.personalInfo.province || competitor.personalInfo.region || competitor.personalInfo.country}
                      </p>
                    )}
                    <div style={{ display: 'flex', gap: '15px', fontSize: '12px', color: 'rgba(255, 255, 255, 0.7)' }}>
                      <span>⭐ {competitor.rating}</span>
                      <span>🏆 {competitor.wins}/{competitor.totalMatches}</span>
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <button
                      onClick={() => handleViewProfile(competitor)}
                      style={{
                        background: 'linear-gradient(45deg, #667eea, #764ba2)',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '16px',
                        fontSize: '12px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.transform = 'scale(1.05)';
                        e.currentTarget.style.boxShadow = '0 5px 15px rgba(102, 126, 234, 0.4)';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.transform = 'scale(1)';
                        e.currentTarget.style.boxShadow = 'none';
                      }}
                    >
                      👤 View Profile
                    </button>
                    <button
                      onClick={() => handleCompetitorSelect(competitor)}
                      style={{
                        background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
                        color: 'white',
                        border: 'none',
                        padding: '10px 20px',
                        borderRadius: '25px',
                        cursor: 'pointer',
                        fontSize: '14px',
                        fontWeight: '600'
                      }}
                    >
                      Challenge
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            {/* Test button for development */}
            <div style={{ textAlign: 'center', marginTop: '30px' }}>
              <button
                onClick={testCompeteFlow}
                style={{
                  background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                  color: 'white',
                  border: 'none',
                  padding: '15px 30px',
                  borderRadius: '25px',
                  cursor: 'pointer',
                  fontSize: '16px',
                  fontWeight: '600'
                }}
              >
                🧪 Test: Skip to Categories
              </button>
            </div>
          </div>
        )}

        {challengeStep === 'chat' && (
          <div style={{ textAlign: 'center', color: 'white' }}>
            <h2>Chat with {competitors.find(c => c.id === '1')?.name || 'Competitor'}</h2>
            <p>This is where you would chat with your opponent before starting the competition.</p>
            <p>For demo purposes, click the button below to proceed to category selection.</p>
            <button
              onClick={() => setChallengeStep('categories')}
              style={{
                background: 'linear-gradient(45deg, #4caf50, #8bc34a)',
                color: 'white',
                border: 'none',
                padding: '15px 30px',
                borderRadius: '25px',
                cursor: 'pointer',
                fontSize: '16px',
                fontWeight: '600',
                marginTop: '20px'
              }}
            >
              Proceed to Challenge Setup
            </button>
          </div>
        )}

        {challengeStep === 'categories' && (
          <CategorySelection
            onSelect={handleCategorySelect}
            onBack={previousStep}
          />
        )}

        {challengeStep === 'mode' && (
          <ModeSelection
            onSelect={handleModeSelect}
            onBack={previousStep}
          />
        )}

        {challengeStep === 'stake' && (
          <StakeSetting
            onConfirm={handleStakeConfirm}
            onBack={previousStep}
            userBalance={2450}
            selectedCategory={selectedCategory}
            selectedMode={selectedMode}
          />
        )}

        {challengeStep === 'waiting' && (
          <WaitingRoom
            competitor={activeCompetitor}
            roomId={competitionData?.id || ''}
            competitionId={competitionData?.id || ''}
            onBack={previousStep}
            onCountdownComplete={handleCountdownComplete}
          />
        )}

        {challengeStep === 'competition' && (
          <>
            {console.log('Rendering competition step with:', {
              challengeStep,
              competitionData: !!competitionData,
              currentQuestion,
              questionsLength: competitionData?.questions?.length,
              question: competitionData?.questions?.[currentQuestion],
              selectedAnswer,
              timeLeft
            })}
            
            {/* Safety check: prevent rendering if question index is out of bounds */}
            {competitionData?.questions && 
             (currentQuestion < 0 || currentQuestion >= competitionData.questions.length) && (
              <div style={{ textAlign: 'center', color: 'white', padding: '50px' }}>
                <h2>Question Index Error</h2>
                <p>The current question index ({currentQuestion}) is out of bounds.</p>
                <p>Valid range: 0 to {competitionData.questions.length - 1}</p>
                <div style={{ marginTop: '20px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  <p>Debug Info:</p>
                  <p>Current Question: {currentQuestion}</p>
                  <p>Questions Length: {competitionData.questions.length}</p>
                  <p>Valid Range: 0 to {competitionData.questions.length - 1}</p>
                </div>
                <button
                  onClick={() => {
                    console.log('Resetting competition due to question index error');
                    setChallengeStep('dashboard');
                    setCurrentQuestion(0);
                  }}
                  style={{
                    background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    cursor: 'pointer',
                    marginTop: '20px'
                  }}
                >
                  Reset Competition
                </button>
              </div>
            )}
            
            {competitionData && competitionData.questions && competitionData.questions.length > 0 && 
             currentQuestion >= 0 && currentQuestion < competitionData.questions.length &&
             competitionData.questions[currentQuestion] ? (
              <div>
                {(() => {
                  try {
                    console.log('Passing props to LiveCompetition:', {
                      questionNumber: currentQuestion + 1,
                      totalQuestions: competitionData.questions.length,
                      timeLeft,
                      selectedAnswer,
                      currentQuestion
                    });
                    return (
                      <LiveCompetition
                        key={`question-${currentQuestion}`}
                        question={competitionData.questions[currentQuestion]}
                        questionNumber={currentQuestion + 1}
                        totalQuestions={competitionData.questions.length}
                        timeLeft={timeLeft}
                        selectedAnswer={selectedAnswer}
                        onAnswerSelect={handleAnswerSelect}
                        onAnswerSubmit={handleAnswerSubmit}
                        onBack={previousStep}
                      />
                    );
                  } catch (error) {
                    console.error('Error rendering LiveCompetition:', error);
                    return (
                      <div style={{ textAlign: 'center', color: 'white', padding: '50px' }}>
                        <h2>Error Loading Competition</h2>
                        <p>There was an error loading the competition component.</p>
                        <button
                          onClick={() => {
                            console.log('Error recovery clicked');
                            setChallengeStep('waiting');
                          }}
                          style={{
                            background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                            color: 'white',
                            border: 'none',
                            padding: '10px 20px',
                            borderRadius: '25px',
                            cursor: 'pointer',
                            marginTop: '20px'
                          }}
                        >
                          Return to Waiting Room
                        </button>
                      </div>
                    );
                  }
                })()}
              </div>
            ) : (
              <div style={{ textAlign: 'center', color: 'white', padding: '50px' }}>
                <h2>Loading Competition...</h2>
                <p>Preparing your questions...</p>
                <div style={{ marginTop: '20px', fontSize: '14px', color: 'rgba(255, 255, 255, 0.7)' }}>
                  <p>Debug Info:</p>
                  <p>Competition Data: {competitionData ? 'Available' : 'Missing'}</p>
                  <p>Questions: {competitionData?.questions ? `${competitionData.questions.length} questions` : 'No questions'}</p>
                  <p>Current Question: {currentQuestion}</p>
                  <p>Question Bounds: {competitionData?.questions ? `0 to ${competitionData.questions.length - 1}` : 'N/A'}</p>
                  <p>Question Exists: {competitionData?.questions?.[currentQuestion] ? 'Yes' : 'No'}</p>
                </div>
                <button
                  onClick={() => {
                    console.log('Manual retry clicked');
                    if (competitionData) {
                      setChallengeStep('competition');
                      // Don't reset currentQuestion here as it interferes with progression
                      // setCurrentQuestion(0);
                      setTimeLeft(30);
                    }
                  }}
                  style={{
                    background: 'linear-gradient(45deg, #ff6b6b, #ee5a24)',
                    color: 'white',
                    border: 'none',
                    padding: '10px 20px',
                    borderRadius: '25px',
                    cursor: 'pointer',
                    marginTop: '20px'
                  }}
                >
                  Retry Competition
                </button>
                {console.error('Missing competition data or invalid question index:', { 
                  competitionData, 
                  currentQuestion, 
                  questionsLength: competitionData?.questions?.length,
                  questionExists: !!competitionData?.questions?.[currentQuestion]
                })}
              </div>
            )}
          </>
        )}

        {challengeStep === 'evaluation' && (
          <ShipAnimation
            userScore={finalResults?.userScore || 0}
            opponentScore={finalResults?.opponentScore || 0}
            totalQuestions={competitionData?.questions.length || 0}
            won={finalResults?.won || false}
            prize={finalResults?.prize || 0}
            captainLogs={captainLogs}
            onDownloadLogs={downloadCaptainLogs}
            onFinish={closeCompetition}
          />
        )}

        {/* View Profile Modal */}
        {showViewProfileModal && selectedProfileCompetitor && (
          <ViewProfileModal
            isOpen={showViewProfileModal}
            onClose={handleViewProfileModalClose}
            competitor={selectedProfileCompetitor}
          />
        )}
      </ContentArea>
    </InterfaceOverlay>
  );
};

export default CompetitionInterface;
